import React from "react";
import Head from "next/head";
import Link from "next/link";
import Navigation from "@/layout/navigation";
import WhoWeServeFeatureSection from "@/components/who-we-serve/WhoWeServeFeatureSection";

export default function ForNutritionistsPage() {
  return (
    <>
      <Head>
        <title>Featcal for Nutritionists | Better follow-up, less admin</title>
        <meta
          name="description"
          content="Featcal helps nutritionists run structured follow-up: food logs, plans, and review-ready insights that improve adherence between sessions across weight loss and weight gain."
        />
      </Head>

      <header className="bg-gradient-to-b from-white from-20% to-blue-300 flex flex-col gap-16 items-center justify-between overflow-hidden px-8 xl:px-0 z-[51] relative pb-20">
        <Navigation />
        <div className="flex flex-col md:items-center md:justify-center gap-6 mt-2 z-10 md:text-center">
          <p className="text-sm font-semibold text-blue-700 bg-white/70 border border-blue-200 px-4 py-2 rounded-full w-fit md:mx-auto">
            For nutrition professionals
          </p>
          <h1 className="text-4xl md:text-5xl font-medium leading-tight max-w-3xl">
            Turn between-session chaos into a repeatable follow-up system.
          </h1>
          <p className="text-md max-w-2xl mx-auto text-slate-700">
            Featcal keeps clients consistent between sessions with structured
            tracking, clear plans, and fast review-so you spend less time
            chasing and more time coaching outcomes.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-center">
            <Link
              href="/start"
              className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
            >
              Start for free
            </Link>
            <Link
              href="/#faq"
              className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
            >
              See FAQs
            </Link>
          </div>
        </div>
      </header>

      <main id="main-content" className="bg-white">
        <section className="py-16 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-slate-200 rounded-3xl p-8">
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
              The real challenge: consistency between sessions
            </h2>
            <ul className="mt-4 space-y-3 text-slate-700">
              {[
                "Food logs take too long to review.",
                "Plans get lost-clients don’t know what to do next.",
                "Adherence drops between sessions, then sessions become catch-up.",
              ].map((p) => (
                <li key={p} className="flex gap-3">
                  <span className="text-slate-400 font-bold">•</span>
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-8">
            {[
              {
                title: "Faster food log review",
                desc: "Clean summaries and patterns you can act on-without manual decoding or spreadsheets.",
              },
              {
                title: "Higher adherence between sessions",
                desc: "Clients know what to do next with in-app plans, reminders, and daily accountability.",
              },
              {
                title: "A clearer ongoing program",
                desc: "Deliver plans, resources, and feedback in one place so the value of follow-up is easier to understand and sell.",
              },
            ].map((card) => (
              <div
                key={card.title}
                className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30"
              >
                <h2 className="text-xl font-semibold text-slate-900 mb-3">
                  {card.title}
                </h2>
                <p className="text-slate-700 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="py-20 px-8 xl:px-0 pt-0">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
            <div className="bg-white border border-slate-200 rounded-3xl p-8">
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-4">
                Great fit for
              </h2>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Weight loss or weight gain programs with weekly reviews.",
                  "Behavior change (protein targets, meal timing, habits).",
                  "Digestive health tracking with symptom + food patterns.",
                  "Sports nutrition without spreadsheet chaos.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-green-600 font-bold">✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-100 rounded-3xl p-8 shadow-xl shadow-blue-100/30">
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">
                What Featcal replaces
              </h3>
              <ul className="space-y-3 text-slate-700">
                {[
                  "Manual food log decoding and spreadsheets.",
                  "Scattered screenshots, DMs, and notes.",
                  "Lost plans and “what do I do next?” messages.",
                  "Chasing adherence between sessions.",
                ].map((p) => (
                  <li key={p} className="flex gap-3">
                    <span className="text-slate-400 font-bold">•</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <WhoWeServeFeatureSection
          eyebrow="Food logs"
          title="Review in minutes, not hours"
          description="Get a clean view of entries and patterns so sessions focus on decisions-without manual decoding."
          bullets={[
            "Spot patterns and gaps faster.",
            "Prepare sessions with a quick snapshot.",
            "Spend more time coaching, less time sorting data.",
          ]}
          imageSrc="/images/user-food-log.webp"
          imageAlt="Food log view preview"
          align="left"
          imageFit="contain"
        />

        <section className="bg-gradient-to-b from-white to-blue-50 py-20 px-8 xl:px-0">
          <div className="max-w-6xl mx-auto bg-white border border-blue-100 rounded-3xl p-8 md:p-10 shadow-xl shadow-blue-100/30">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
                  Ready to improve adherence between sessions?
                </h2>
                <p className="text-slate-700 mt-2">
                  Start free and invite a client in minutes.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  href="/start"
                  className="bg-blue-600 text-white py-3 px-8 rounded-full font-semibold hover:bg-blue-700 transition-all duration-200 text-center"
                >
                  Start for free
                </Link>
                <Link
                  href="/#faq"
                  className="bg-white text-slate-900 py-3 px-8 rounded-full font-semibold border border-blue-200 hover:bg-blue-50 transition-all duration-200 text-center"
                >
                  See FAQs
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
